import { Clock } from "lucide-react";
import { Card } from "@/components/ui/card";
import { getPublicAvailabilityLabel } from "@/lib/publicAvailability";

interface TimeSlotCardProps {
  time: string;
  available: number;
  total?: number;
  selected?: boolean;
  onSelect?: () => void;
  onClick?: () => void;
  disabled?: boolean;
  unavailableReason?: 'full' | 'capacity' | 'hours' | 'incompatible' | 'none';
}

export const TimeSlotCard = ({ 
  time, 
  available, 
  total, 
  selected, 
  onSelect,
  onClick,
  disabled = false,
  unavailableReason = 'none'
}: TimeSlotCardProps) => {
  const isUnavailable = unavailableReason !== 'none' || disabled;
  const handleClick = onClick || onSelect;
  const statusLabel = getPublicAvailabilityLabel(
    disabled && unavailableReason === 'none' ? 'full' : unavailableReason === 'none' ? 'available' : unavailableReason,
  );

  const getCardStyle = () => {
    if (unavailableReason === 'full' || disabled) {
      return "border-red-300 bg-red-50/50 dark:border-red-800 dark:bg-red-950/20";
    }
    if (unavailableReason === 'capacity') {
      return "border-amber-300 bg-amber-50/50 dark:border-amber-700 dark:bg-amber-950/20";
    }
    if (unavailableReason === 'hours') {
      return "border-muted bg-muted/30";
    }
    // Disponible
    return "border-green-300 bg-green-50/50 dark:border-green-700 dark:bg-green-950/20 hover:border-green-400";
  };

  return (
    <Card
      role="button"
      aria-disabled={isUnavailable}
      aria-label={`${time}, ${statusLabel}`}
      className={`p-3 sm:p-4 cursor-pointer transition-all hover:shadow-lg ${getCardStyle()} ${
        selected ? "ring-2 ring-primary" : ""
      } ${isUnavailable ? "opacity-60 cursor-not-allowed" : ""}`}
      onClick={!isUnavailable ? handleClick : undefined}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-primary flex-shrink-0" />
          <span className="font-serif text-sm sm:text-base font-semibold">{time}</span>
        </div>
        <span className="text-xs font-medium text-muted-foreground text-right">
          {statusLabel}
        </span>
      </div>
    </Card>
  );
};